import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  ActivityBodyInterface,
  ActivityMediaInterface,
  ActivityStatusEnum,
  BookingItemResourceInterface,
  BookingItemStatusEnum,
  BookingStatus,
  CreateTimeslotBodyInterface,
  FileTypeEnum,
  ImageResolutionInterface,
  OrganizationInterface,
  PaginatedResponseInterface,
  TimeslotFilterParamsInterface,
  TimeslotStatusEnum,
  UpdateBulkTimeslotBodyInterface,
  UpdateBulkTimeslotWorkflowBody,
  UpdateTimeslotBodyInterface,
} from '@sport-activities/sdk';
import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { ACTIVITIES_BASE_URL } from '~/core/core.module';
import { createHttpParams } from '~/core/helpers/create-http-params/create-http-params.helper';
import { getValueByLocale } from '~/core/helpers/model/get-value-by-locale/get-value-by-locale.helper';
import { toDecimalPrice, toIntegerPrice } from '~/core/helpers/numbers.helper';
import { Logger } from '~/core/logger.service';
import { Activity } from '~/core/model/activity.model';
import { Timeslot } from '~/core/model/timeslot.model';
import { ActivityService } from '~/core/services/activity/activity.service';
import { buildFormDataForMediaUpload } from '~/organizer/shared/helpers/media/media.helper';
import { BookingItemsGroupByUserIdInterface } from '~/organizer/shared/services/activity/interfaces/booking-items-group-by-user-id.interface';
import { ToggleService } from '~/toggles/services/toggle.service';

import { environment } from 'src/environments/environment';
import { LocalizeRouterService } from '@gilsdav/ngx-translate-router';

@Injectable({
  providedIn: 'root',
})
export class OrganizerSharedActivityService extends ActivityService {
  private readonly currentLocale: string;
  private readonly fallbackLocale = environment.fallbackLocale || 'en';
  public activity: Activity;

  constructor(
    @Inject(ACTIVITIES_BASE_URL) protected readonly activitiesUrl: string,
    protected readonly httpClient: HttpClient,
    protected readonly localizeRouterService: LocalizeRouterService,
    protected readonly logger: Logger,
    protected readonly toggleService: ToggleService,
    private readonly translateService: TranslateService
  ) {
    super(
      activitiesUrl,
      httpClient,
      localizeRouterService,
      logger,
      toggleService
    );
    this.currentLocale = this.translateService.currentLang;
  }

  private setActivity(partialActivity: Partial<ActivityBodyInterface>): void {
    if (!this.hasActivity()) {
      this.activity = new Activity({});
    }

    if (partialActivity.name !== undefined) {
      this.activity.name = getValueByLocale<string>(
        partialActivity.name,
        this.currentLocale,
        this.fallbackLocale
      );
    }
    if (partialActivity.sports !== undefined) {
      this.activity.sports = partialActivity.sports;
    }
    if (partialActivity.description !== undefined) {
      this.activity.description = getValueByLocale<string>(
        partialActivity.description,
        this.currentLocale,
        this.fallbackLocale
      );
    }
  }

  public addMedia(
    activityIdentifier: number | string,
    media: Blob,
    name: string,
    fileTypes: FileTypeEnum[],
    resolutions?: ImageResolutionInterface[]
  ): Observable<ActivityMediaInterface> {
    const formData = buildFormDataForMediaUpload(
      media,
      name,
      fileTypes,
      resolutions
    );

    return this.httpClient.post<ActivityMediaInterface>(
      `${this.activitiesUrl}/activities/${activityIdentifier}/medias`,
      formData
    );
  }

  public clearActivity(): void {
    this.activity = null;
  }

  public deleteMedia(
    activityIdentifier: number | string,
    mediaIdentifier: string
  ): Observable<void> {
    return this.httpClient.delete<void>(
      `${this.activitiesUrl}/activities/${activityIdentifier}/medias/${mediaIdentifier}`
    );
  }

  public getActivitiesByOrganizationId(
    organizationId: string,
    timeslots?: TimeslotFilterParamsInterface,
    status?: ActivityStatusEnum,
    plannable?: boolean
  ): Observable<PaginatedResponseInterface<Activity>> {
    return this.getActivities({
      organizationIdentifier: organizationId,
      pagination: {
        limit: 100,
      },
      timeslots,
      status,
      plannable,
    }).pipe(
      map((response) => {
        response.items = response.items.filter(
          (activity) =>
            activity.status === ('PUBLISHED' as ActivityStatusEnum) ||
            activity.status === ('DRAFT' as ActivityStatusEnum)
        );

        return response;
      })
    );
  }

  public getTimeslotBookingItems(
    timeslotId: string,
    bookingItemStatus?: BookingItemStatusEnum
  ): Observable<BookingItemResourceInterface[]> {
    const params = {
      bookingStatus: 'COMPLETED' as BookingStatus,
      bookingItemStatus: undefined,
    };
    if (bookingItemStatus) {
      params.bookingItemStatus = bookingItemStatus;
    }

    return this.httpClient.get<BookingItemResourceInterface[]>(
      `${this.activitiesUrl}/timeslots/${timeslotId}/booking-items`,
      {
        params: createHttpParams(params),
      }
    );
  }

  public getTimeslotBookingItemsByUserId(
    timeslotId: string,
    userId: string
  ): Observable<BookingItemsGroupByUserIdInterface> {
    return this.getTimeslotBookingItemsGroupByUserId(timeslotId).pipe(
      map((bookingItemsGroupByUserId) =>
        bookingItemsGroupByUserId.find(
          (bookingItemGroupByUserId) =>
            bookingItemGroupByUserId.userId === userId
        )
      )
    );
  }

  public getTimeslotBookingItemsGroupByUserId(
    timeslotId: string,
    bookingItemStatus?: BookingItemStatusEnum
  ): Observable<BookingItemsGroupByUserIdInterface[]> {
    return this.getTimeslotBookingItems(timeslotId, bookingItemStatus).pipe(
      map((bookingItems) => {
        const bookingItemsGroupByUserId: BookingItemsGroupByUserIdInterface[] =
          [];

        bookingItems.forEach((bookingItem) => {
          const totalAttendees = bookingItem.attendees?.length;
          const totalPrice = toDecimalPrice(
            toIntegerPrice(bookingItem.price * totalAttendees)
          );

          const currentBookingItemGroupByUserId =
            bookingItemsGroupByUserId.find(
              (bookingItemGroupByUserId) =>
                bookingItemGroupByUserId.userId === bookingItem.userId
            );

          if (currentBookingItemGroupByUserId) {
            currentBookingItemGroupByUserId.bookingItems.push(bookingItem);
            currentBookingItemGroupByUserId.totalAttendees += totalAttendees;
            currentBookingItemGroupByUserId.totalPrice += totalPrice;
          } else {
            bookingItemsGroupByUserId.push({
              bookingItems: [bookingItem],
              totalAttendees,
              totalPrice,
              userId: bookingItem.userId,
            });
          }
        });

        return bookingItemsGroupByUserId;
      })
    );
  }

  public getTimeslotsByOrganizationId(
    organizationId: string,
    filters: TimeslotFilterParamsInterface = {}
  ): Observable<Timeslot[]> {
    return this.getTimeslots({
      ...filters,
      organizationIdentifier: organizationId,
      timeslotStatus: 'PUBLISHED' as TimeslotStatusEnum,
    }).pipe(map((response) => response.items));
  }

  public hasActivity(): boolean {
    return Boolean(this.activity);
  }

  public patchActivity(
    activity: Partial<ActivityBodyInterface>
  ): Observable<null> {
    this.setActivity(activity);

    return this.patchActivityById(this.activity.sku, activity);
  }

  public patchActivityById(
    activitySku: number | string,
    activity: Partial<ActivityBodyInterface>
  ): Observable<null> {
    return this.httpClient.patch<null>(
      `${this.activitiesUrl}/activities/${activitySku}`,
      activity
    );
  }

  public patchTimeslot(
    timeslotId: string,
    timeslot: Partial<UpdateTimeslotBodyInterface>
  ): Observable<null> {
    return this.httpClient.patch<null>(
      `${this.activitiesUrl}/timeslots/${timeslotId}`,
      timeslot
    );
  }

  public patchTimeslotBulk(
    activityId: string,
    timeslots: UpdateBulkTimeslotBodyInterface[]
  ): Observable<string[]> {
    return this.httpClient.patch<string[]>(
      `${this.activitiesUrl}/activities/${activityId}/timeslots/_bulk`,
      timeslots
    );
  }

  public postActivity(
    activity: Partial<ActivityBodyInterface>
  ): Observable<number> {
    this.setActivity(activity);

    return this.httpClient
      .post<number>(`${this.activitiesUrl}/activities`, activity)
      .pipe(
        tap((activityId) => {
          this.activity.sku = activityId;
        })
      );
  }

  public postActivityAction(
    activityIdentifier: number | string,
    action: string
  ): Observable<null> {
    return this.httpClient.post<null>(
      `${this.activitiesUrl}/activities/${activityIdentifier}/workflow/${action}`,
      null
    );
  }

  public postActivityOnOrganization(
    activity: Partial<ActivityBodyInterface>,
    organization: OrganizationInterface
  ): Observable<number> {
    const accountabilityReference =
      organization.paymentProviderIdentifier || organization.storeNumber;
    activity.organizationIdentifier = organization.identifier;
    activity.accountabilityReference = accountabilityReference;

    return this.postActivity(activity).pipe(
      tap(() => {
        this.activity.organizationIdentifier = organization.identifier;
        this.activity.accountabilityReference = accountabilityReference;
      })
    );
  }

  public postTimeslot(
    timeslot: CreateTimeslotBodyInterface
  ): Observable<string> {
    return this.httpClient.post<string>(
      `${this.activitiesUrl}/timeslots`,
      timeslot
    );
  }

  public postTimeslotAction(
    timeslotIdentifier: string,
    action: string
  ): Observable<null> {
    return this.httpClient.post<null>(
      `${this.activitiesUrl}/timeslots/${timeslotIdentifier}/workflow/${action}`,
      null
    );
  }

  public postTimeslotsBulk(
    timeslots: CreateTimeslotBodyInterface[]
  ): Observable<string[]> {
    if (timeslots?.length === 0) {
      return of([]);
    }

    const activityIdentifier = timeslots?.[0]?.activityIdentifier;

    return this.httpClient.post<string[]>(
      `${this.activitiesUrl}/activities/${activityIdentifier}/timeslots/_bulk`,
      timeslots
    );
  }

  public updateTimeslotWorkflowBulk(
    activityId: number | string,
    timeslotActions: UpdateBulkTimeslotWorkflowBody[]
  ): Observable<null> {
    return this.httpClient.post<null>(
      `${this.activitiesUrl}/activities/${activityId}/timeslot/workflow/_bulk`,
      timeslotActions
    );
  }
}
